import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BsArrowRight, BsChevronLeft, BsChevronRight } from "react-icons/bs";

const defaultProjects = [
  {
    id: "def-1",
    title: "E-Commerce Platform",
    category: "Web Development",
    desc: "High-performance custom e-commerce web platform integrated with automated checkout.",
    image: "/assets/project_ecommerce.jpg",
    link: "/portfolio"
  },
  {
    id: "def-2",
    title: "AI Chat Assistant",
    category: "AI Solutions",
    desc: "Custom intelligent AI chat assistant with real-time customer qualification.",
    image: "/assets/project_aichat.jpg",
    link: "/portfolio"
  },
  {
    id: "def-3",
    title: "Brand Growth Campaign",
    category: "Digital Marketing",
    desc: "Targeted digital marketing funnels, search visibility and paid campaigns.",
    image: "/assets/project_growth.jpg",
    link: "/portfolio"
  },
  {
    id: "def-4",
    title: "SaaS Web Application",
    category: "Web Applications",
    desc: "Modern multi-tenant SaaS application dashboard tailored for business operations.",
    image: "/assets/project_saas.jpg",
    link: "/portfolio"
  }
];

const getProjectImageUrl = (item) => {
  const img = item?.image || item?.img;
  if (!img) return "/assets/project_ecommerce.jpg";
  if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
    return img;
  }
  if (img.startsWith("/assets/") || img.startsWith("assets/")) {
    return img.startsWith("/") ? img : `/${img}`;
  }
  const baseUrl = window.API_URL || "http://localhost:8000";
  const cleanImg = img.replace(/^\/+/, "");
  if (cleanImg.startsWith("uploads/")) {
    return `${baseUrl}/${cleanImg}`;
  }
  return `${baseUrl}/uploads/${cleanImg}`;
};

const getCategory = (item) => {
  if (item.category) return item.category;
  const text = `${item.title || ""} ${item.desc || ""}`.toLowerCase();
  if (text.includes("ai") || text.includes("bot") || text.includes("intelligence") || text.includes("automation")) {
    return "AI Solutions";
  }
  if (text.includes("market") || text.includes("seo") || text.includes("ads") || text.includes("growth") || text.includes("campaign")) {
    return "Digital Marketing";
  }
  if (text.includes("app") || text.includes("saas") || text.includes("portal") || text.includes("erp") || text.includes("crm")) {
    return "Web Applications";
  }
  return "Web Development";
};

const categories = ["All", "Websites", "AI Solutions", "Digital Marketing", "Web-Apps"];

const Projects = ({ limit = 3, isHomePage = false }) => {
  const [activeTab, setActiveTab] = useState("All");
  const [projects, setProjects] = useState(defaultProjects);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const baseUrl = window.API_URL || "http://localhost:8000";
        const res = await axios.get(baseUrl + "/projects");
        if (Array.isArray(res.data) && res.data.length > 0) {
          // Reverse so the newly added projects from Admin panel show first
          const formatted = [...res.data].reverse().map(item => ({
            id: item._id || item.id,
            title: item.title,
            desc: item.desc,
            category: getCategory(item),
            image: getProjectImageUrl(item),
            link: item.link || "/portfolio"
          }));
          setProjects(formatted);
        }
      } catch (err) {
        // Keep default fallback
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Filter projects by category tab
  const filtered = projects.filter(item => {
    if (activeTab === "All") return true;
    if (activeTab === "Websites") return item.category === "Web Development" || item.category === "Websites";
    if (activeTab === "AI Solutions") return item.category === "AI Solutions";
    if (activeTab === "Digital Marketing") return item.category === "Digital Marketing";
    if (activeTab === "Web-Apps") return item.category === "Web Applications" || item.category === "Web-Apps";
    return true;
  });

  // Apply limit if specified (e.g. 3 on Home page)
  const displayedProjects = limit ? filtered.slice(0, limit) : filtered;

  return (
    <section className="py-16 sm:py-24 bg-[#090d16] text-white relative border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header Row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          
          {/* Left: Tag & Heading */}
          <div className="space-y-3" data-aos="fade-right">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm bg-[#f06600]"></span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                {isHomePage ? "FEATURED WORK" : "OUR PORTFOLIO"}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Ideas Turned Into <span className="text-[#f06600]">Real Results</span>
            </h2>
          </div>

          {/* Right: Filter Category Pills */}
          <div className="flex flex-wrap items-center gap-2" data-aos="fade-left">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === cat
                    ? "bg-[#f06600] text-white shadow-md shadow-orange-500/30"
                    : "bg-[#121826] hover:bg-[#1a2337] text-slate-300 border border-slate-700/60"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

        </div>

        {/* Projects Grid: 3 columns if limit===3 on Home, otherwise responsive 3/4 cols */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${limit === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6`}>
          {displayedProjects.map((proj, idx) => {
            const isExternalLink = proj.link && (proj.link.startsWith("http://") || proj.link.startsWith("https://"));

            return isExternalLink ? (
              <a
                key={proj.id}
                href={proj.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#101625] hover:bg-[#131b2e] border border-slate-800 hover:border-[#f06600]/60 rounded-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1 shadow-xl flex flex-col justify-between group"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                {/* Image Showcase Container */}
                <div className="relative aspect-[16/11] overflow-hidden bg-slate-900 border-b border-slate-800/80">
                  <img 
                    src={proj.image} 
                    alt={proj.title}
                    className="w-full h-full object-cover object-top origin-top group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/project_ecommerce.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101625]/60 via-transparent to-transparent pointer-events-none"></div>
                </div>

                {/* Card Bottom Meta */}
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#f06600] transition-colors leading-snug">
                      {proj.title}
                    </h3>
                    <p className="text-xs text-slate-400 font-medium mt-1">
                      {proj.category}
                    </p>
                  </div>

                  <div className="w-8 h-8 rounded-full bg-[#182138] border border-slate-700/60 flex items-center justify-center text-slate-300 group-hover:bg-[#f06600] group-hover:text-white group-hover:border-[#f06600] transition-all shrink-0 ml-2">
                    <BsArrowRight className="text-sm group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            ) : (
              <Link
                key={proj.id}
                to={proj.link}
                className="bg-[#101625] hover:bg-[#131b2e] border border-slate-800 hover:border-[#f06600]/60 rounded-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1 shadow-xl flex flex-col justify-between group"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                {/* Image Showcase Container */}
                <div className="relative aspect-[16/11] overflow-hidden bg-slate-900 border-b border-slate-800/80">
                  <img 
                    src={proj.image} 
                    alt={proj.title}
                    className="w-full h-full object-cover object-top origin-top group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/project_ecommerce.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101625]/60 via-transparent to-transparent pointer-events-none"></div>
                </div>

                {/* Card Bottom Meta */}
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#f06600] transition-colors leading-snug">
                      {proj.title}
                    </h3>
                    <p className="text-xs text-slate-400 font-medium mt-1">
                      {proj.category}
                    </p>
                  </div>

                  <div className="w-8 h-8 rounded-full bg-[#182138] border border-slate-700/60 flex items-center justify-center text-slate-300 group-hover:bg-[#f06600] group-hover:text-white group-hover:border-[#f06600] transition-all shrink-0 ml-2">
                    <BsArrowRight className="text-sm group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Home Page: View All Work CTA button */}
        {isHomePage && (
          <div className="mt-12 text-center" data-aos="fade-up">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 bg-[#121826] hover:bg-[#f06600] text-slate-200 hover:text-white border border-slate-700/80 hover:border-[#f06600] font-bold px-7 py-3.5 rounded-full text-xs uppercase tracking-wider transition-all duration-300 shadow-lg hover:shadow-orange-500/25 group"
            >
              <span>View All Projects in Portfolio</span>
              <BsArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}

      </div>
    </section>
  );
};

export default Projects;
